import { useCallback, useSyncExternalStore } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  runStateStore,
  activeFocusedRunIdStore,
} from '../../hooks/useAsyncInvokeAdapter';
import { DURATION_SLOW } from '@/lib/animation';

export function ContextUtilizationBar() {
  // Track which run is currently focused so the bar shows that run's data.
  const activeRunId = useSyncExternalStore(
    activeFocusedRunIdStore.subscribe,
    activeFocusedRunIdStore.getSnapshot,
  );

  const subscribe = useCallback(
    (listener: () => void) => runStateStore.subscribe(activeRunId, listener),
    [activeRunId],
  );
  const getSnapshot = useCallback(
    () => runStateStore.getSnapshot(activeRunId).contextUtilization,
    [activeRunId],
  );
  const {
    utilizationPercent,
    compactionThresholdPercent,
    compactionImminent,
    isCompacting,
  } = useSyncExternalStore(subscribe, getSnapshot);

  // Dynamic watermarks derived from backend-provided threshold
  const threshold = compactionThresholdPercent ?? 80;
  const showAt = threshold * 0.75;
  const criticalAt = Math.min(threshold * 1.1, 98);

  if (utilizationPercent == null || utilizationPercent < showAt) return null;

  const isCritical = utilizationPercent >= criticalAt || utilizationPercent >= 95;
  const isWarning = compactionImminent && !isCritical;

  // Color scheme
  const barColor = isCritical
    ? 'bg-red-500/60'
    : isWarning
      ? 'bg-orange-500/70'
      : 'bg-amber-500/40';

  const label = isCritical
    ? 'Context almost full'
    : isWarning
      ? 'Context nearing limit'
      : null;

  const barHeight = isWarning || isCritical ? 'h-[3px]' : 'h-[2px]';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: DURATION_SLOW }}
        className="relative w-full shrink-0"
        title={`Context: ${Math.round(utilizationPercent)}% (compacts at ${threshold}%)`}
      >
        {/* Track */}
        <div className={`w-full ${barHeight} bg-muted/30 overflow-hidden rounded-full`}>
          {/* Fill */}
          <motion.div
            className={`${barHeight} ${barColor} rounded-full ${isCompacting ? 'animate-pulse' : ''}`}
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(utilizationPercent, 100)}%` }}
            transition={{ duration: DURATION_SLOW * 1.5, ease: 'easeOut' }}
          />
        </div>

        {/* Label (only for warning/critical) */}
        {label && (
          <motion.div
            initial={{ opacity: 0, y: -2 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute right-0 top-1 text-[10px] leading-none text-muted-foreground"
          >
            {label}
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
